import React from 'react';
import { Prompt } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { Typography, Grid, TextField, Box } from '@mui/material';

import SetPageTitle from 'components/Portals/SetPageTitle';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import SaveButton from 'common/components/SaveButton';

import { api } from 'common/frontend/api';

import { ShopPublicInfo } from 'common/types';
import Container from 'components/Container';
import ContainerBox from 'components/ContainerBox';
import ImageUpload from 'components/ImageUpload';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';

import { Routes, useTypedLocation } from 'routing';
import { useTranslation } from 'services/localization';
import Branding from './components/Branding';

import HasFeature from 'components/features/HasFeature';
import { IndustryCategory } from 'common/services/analytics/tractionAnalytics';
import IndustrySelector from 'components/IndustrySelector';
import { isValidHexColor } from 'common/utils/validation';
import AddOnPromoBlock from 'components/features/promos/AddOnPromoBlock';
import AutoCompleteCountryPicker from 'common/components/pickers/AutoCompleteCountryPicker';
import { setOnboardingActionDone } from 'services/onboarding/utils';

type FormValues = {
	publicInfo: Partial<ShopPublicInfo>;
	industryCategories: IndustryCategory[];
};

const AccountGeneral = () => {
	const { t } = useTranslation();
	const { state } = useTypedLocation();
	const dispatch = useDispatch();
	const publicInfo = useSelector(ShopSelectors.activeShopPublicInfo)!;
	const activeShopAnalytics = useSelector(ShopSelectors.activeShopAnalytics);
	const shopId = useSelector(ShopSelectors.activeShopId)!;

	const defaultValues: FormValues = {
		publicInfo,
		industryCategories: activeShopAnalytics?.industryCategories ?? [],
	};

	const shopInfoForm = useForm<FormValues>({
		defaultValues,
		mode: 'onChange',
	});

	const { isDirty, isValid, isSubmitting, dirtyFields } = shopInfoForm.formState;

	const updateOnboardingActions = () => {
		if (!!dirtyFields.publicInfo?.businessId) {
			setOnboardingActionDone('BUSINESS_ID', activeShopAnalytics);
		}
		if (!!dirtyFields.publicInfo?.logoImgUrl) {
			setOnboardingActionDone('LOGO', activeShopAnalytics);
		}
	};

	const onSubmit = async (data: FormValues) => {
		try {
			await api().shops.doc(shopId).public.update(data.publicInfo);

			if (!!dirtyFields.industryCategories && !!activeShopAnalytics?.id) {
				await api()
					.shopAnalytics.doc(activeShopAnalytics.id)
					.update({ industryCategories: data.industryCategories });
			}
			updateOnboardingActions();
			shopInfoForm.reset(data);
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
	};

	return (
		<>
			<SetPageTitle
				priority={2}
				title={t('account.general.title', 'General')}
				rightContent={
					<SaveButton
						disabled={!isDirty || !isValid}
						loading={isSubmitting}
						onClick={shopInfoForm.handleSubmit(onSubmit)}
						t={t}
					/>
				}
			/>
			<SetPageNavAction
				priority={2}
				backAction={
					state?.from === Routes.HOME_VIEW
						? { to: Routes.HOME_VIEW, title: t('common:actions.home', 'Home') }
						: { to: Routes.ACCOUNT, title: t('account.pageTitle', 'Account') }
				}
				quickLinks={[]}
			/>
			<Prompt when={isDirty} message={() => t('common:prompts.unsavedChanges')} />
			<Container>
				<HasFeature requiredFeature="LEGAL_SETTINGS">
					<ContainerBox>
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<Typography variant="h6">
									{t('account.general.businessInformation', 'Business information')}
								</Typography>
							</Grid>
							<Grid item xs={12} md={6}>
								<Controller
									control={shopInfoForm.control}
									name={'publicInfo.name'}
									defaultValue=""
									render={(renderProps) => (
										<TextField
											label={t('common:form.businessName', 'Business name')}
											helperText={t(
												'common:form.businessNameHelper2',
												'The public name of your business',
											)}
											fullWidth
											value={renderProps.field.value}
											onChange={(e) => renderProps.field.onChange(e.target.value)}
											variant="outlined"
										/>
									)}
								/>
								<Box mt={4} />
								<Controller
									control={shopInfoForm.control}
									name={'publicInfo.businessId'}
									defaultValue=""
									render={(renderProps) => (
										<TextField
											label={t('common:form.businessId', 'Business ID')}
											helperText={t(
												'account.general.businessIdHelper',
												'Your public business ID, which is shown to customers during checkout and in receipts',
											)}
											placeholder="1234567-8"
											fullWidth
											value={renderProps.field.value}
											onChange={(e) => renderProps.field.onChange(e.target.value)}
											variant="outlined"
										/>
									)}
								/>
								<Box mt={4} />
								<Controller
									control={shopInfoForm.control}
									name={'publicInfo.email'}
									defaultValue=""
									render={(renderProps) => (
										<TextField
											label={t('common:form.email')}
											helperText={t('common:form.publicEmailAddress', 'Public email address')}
											fullWidth
											value={renderProps.field.value}
											onChange={(e) => renderProps.field.onChange(e.target.value)}
											variant="outlined"
										/>
									)}
								/>
								<Box mt={4} />
								<Controller
									control={shopInfoForm.control}
									name={'publicInfo.phone'}
									defaultValue=""
									render={(renderProps) => (
										<TextField
											label={t('common:form.phoneNumber')}
											helperText={t('common:form.publicPhoneNumber', 'Public phone number')}
											fullWidth
											value={renderProps.field.value}
											onChange={(e) => renderProps.field.onChange(e.target.value)}
											variant="outlined"
										/>
									)}
								/>
								<Box mt={4} />
								<Controller
									control={shopInfoForm.control}
									name={'publicInfo.country'}
									defaultValue=""
									render={(renderProps) => (
										<AutoCompleteCountryPicker
											variant="outlined"
											label={t('common:address.country')}
											id="country-select"
											value={renderProps.field.value ?? ''}
											onChange={renderProps.field.onChange}
										/>
									)}
								/>
								<Box mt={4} />
								<Controller
									control={shopInfoForm.control}
									name="industryCategories"
									defaultValue={defaultValues.industryCategories}
									render={(renderProps) => (
										<IndustrySelector
											label={t('account.general.industrySegments', 'Select industry segment')}
											value={renderProps.field.value}
											onChange={renderProps.field.onChange}
											variant="outlined"
										/>
									)}
								/>
							</Grid>
						</Grid>
						<Box mt={4} />
					</ContainerBox>
				</HasFeature>
				<ContainerBox>
					<Grid id="appearance" container spacing={2}>
						<Grid item xs={12} md={6}>
							<Typography variant="h6">{t('account.general.appearance', 'Appearance')}</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<Controller
								control={shopInfoForm.control}
								name={'publicInfo.logoImgUrl'}
								render={(renderProps) => (
									<ImageUpload
										label={t('shop.logo', 'Logo')}
										value={renderProps.field.value}
										onChange={(url) => {
											const imgUrl = !url ? undefined : Array.isArray(url) ? url[0] : url;
											renderProps.field.onChange(imgUrl);
										}}
										shopId={shopId}
									/>
								)}
							/>
						</Grid>
						<HasFeature requiredFeature="BRANDING" scope="user">
							<HasFeature scope="shop" requiredFeature="BRANDING">
								<Grid item xs={12} md={6}>
									<>
										<Typography variant="h6" gutterBottom>
											{t('shop.branding', 'Branding')}
										</Typography>
										<Typography variant="body2" color="secondary">
											{t('shop.brandingSubheader', 'Global appearance for your sales channels')}
										</Typography>
									</>
								</Grid>
								<Grid item xs={12} md={6}>
									<Controller
										control={shopInfoForm.control}
										name={'publicInfo.customBranding'}
										rules={{
											validate: (value) => {
												return !value?.active || isValidHexColor(value.mainColor);
											},
										}}
										render={(renderProps) => (
											<Branding
												customBranding={renderProps.field.value}
												setBranding={renderProps.field.onChange}
											/>
										)}
									/>
								</Grid>
							</HasFeature>
						</HasFeature>
					</Grid>
				</ContainerBox>
				<HasFeature
					requiredFeature="BRANDING"
					fallback={(failedChecks) => {
						return failedChecks.shop && !failedChecks.user ? (
							<AddOnPromoBlock addOn="BRANDING" />
						) : null;
					}}
				/>
			</Container>
		</>
	);
};

export default AccountGeneral;
